/**
 * lex.ivoliq Jahreserklärungen-Drawer (Phase 3, Juli 2026) — StB-Sicht.
 * E1/E1a (Basispauschalierung, GFB, SVS) + U1-Jahreserklärung + FinanzOnline-XML.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Download, Save, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (v) =>
  v == null ? '—' : new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(v);

const KzTable = ({ rows, testId }) => (
  <table className="w-full text-sm" data-testid={testId}>
    <tbody>
      {rows.map((r) => (
        <tr key={r.code + r.label} className="border-t border-slate-100">
          <td className="py-2 lex-mono text-xs text-slate-400 w-16">KZ {r.code}</td>
          <td className="py-2 text-slate-600 pr-4">{r.label}</td>
          <td className="py-2 lex-mono text-right font-semibold" style={{ color: '#0A1128' }}>{fmtEur(r.value)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const NumInput = ({ label, value, onChange, testId }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1">{label}</label>
    <input type="number" min="0" step="0.01" className="lex-input lex-mono" value={value}
      onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  </div>
);

export default function LexYearEndDrawer({ client, onClose }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear - 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ variante: 'general', svs_beitraege: 0, kirchenbeitrag: 0, spenden: 0, sonstige_sonderausgaben: 0, notes: '' });

  const load = useCallback(async (y) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lex/advisor/clients/${client.client_id}/yearend?year=${y}`);
      setData(res.data);
      const i = res.data.inputs || {};
      setForm({
        variante: i.variante || 'general',
        svs_beitraege: i.svs_beitraege || 0,
        kirchenbeitrag: i.kirchenbeitrag || 0,
        spenden: i.spenden || 0,
        sonstige_sonderausgaben: i.sonstige_sonderausgaben || 0,
        notes: i.notes || '',
      });
    } catch {
      toast.error('Jahresdaten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [client.client_id]);

  useEffect(() => { load(year); }, [year, load]);

  const save = async () => {
    if (form.variante === 'special' && !form.notes.trim()) {
      toast.error('Für die 6 %-Variante ist ein Begründungs-Vermerk erforderlich.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/lex/advisor/clients/${client.client_id}/yearend/inputs`, {
        year,
        variante: form.variante,
        svs_beitraege: parseFloat(form.svs_beitraege) || 0,
        kirchenbeitrag: parseFloat(form.kirchenbeitrag) || 0,
        spenden: parseFloat(form.spenden) || 0,
        sonstige_sonderausgaben: parseFloat(form.sonstige_sonderausgaben) || 0,
        notes: form.notes.trim(),
      });
      toast.success('Eingaben gespeichert — Berechnung aktualisiert.');
      await load(year);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const downloadXml = async (kind) => {
    try {
      const res = await axios.get(
        `${API}/lex/advisor/clients/${client.client_id}/yearend/${kind}-xml?year=${year}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ivoliq_lex_${kind.toUpperCase()}_${year}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'XML-Export fehlgeschlagen.';
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch {}
      }
      toast.error(msg);
    }
  };

  const e1a = data?.e1?.e1a;
  const u1 = data?.u1;
  const warnings = [...(e1a?.warnings || []), ...(u1?.warnings || [])];

  return (
    <div className="fixed inset-0 z-[90] flex justify-end lex-theme" data-testid="lex-yearend-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        {/* Kopf */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: '#C5A059' }}>Jahreserklärungen</p>
            <h2 className="lex-serif text-2xl font-semibold" style={{ color: '#0A1128' }}>{client.name}</h2>
          </div>
          <div className="flex items-center gap-3">
            <select className="lex-input lex-mono !w-28 !py-2" value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="lex-ye-year-select">
              {[currentYear, currentYear - 1, currentYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="lex-ye-close-btn">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="animate-spin h-8 w-8 border-b-2" style={{ borderColor: '#0A1128' }} />
          </div>
        ) : (
          <div className="px-6 py-6 space-y-8">
            {/* Warnungen */}
            {warnings.length > 0 && (
              <div className="space-y-2" data-testid="lex-ye-warnings">
                {warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 text-sm bg-amber-50 border-l-2 border-amber-500 text-amber-800">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {w}
                  </div>
                ))}
              </div>
            )}
            {e1a && !e1a.eligible && (
              <div className="p-3 text-sm bg-red-50 border-l-2 border-red-500 text-red-700" data-testid="lex-ye-not-eligible">
                Basispauschalierung {year} nicht zulässig (Vorjahresumsatz über {fmtEur(e1a.turnover_limit)}).
              </div>
            )}

            {/* StB-Eingaben */}
            <section className="lex-card p-5">
              <h3 className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-4">StB-Eingaben</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1">Pauschal-Variante</label>
                  <select className="lex-input" value={form.variante}
                    onChange={(e) => setForm((f) => ({ ...f, variante: e.target.value }))} data-testid="lex-ye-variante-select">
                    <option value="general">Allgemein ({year >= 2026 ? '15' : year === 2025 ? '13,5' : '12'} %) — Regelfall RA</option>
                    <option value="special">6 % (Sonderfälle: Beratung/§ 22 Z 2/Vortrag)</option>
                  </select>
                </div>
                <NumInput label="SVS-Beiträge (KZ 9225)" value={form.svs_beitraege}
                  onChange={(v) => setForm((f) => ({ ...f, svs_beitraege: v }))} testId="lex-ye-svs-input" />
                <NumInput label="Kirchenbeitrag (max. 600 €)" value={form.kirchenbeitrag}
                  onChange={(v) => setForm((f) => ({ ...f, kirchenbeitrag: v }))} testId="lex-ye-kirche-input" />
                <NumInput label="Spenden" value={form.spenden}
                  onChange={(v) => setForm((f) => ({ ...f, spenden: v }))} testId="lex-ye-spenden-input" />
              </div>
              {form.variante === 'special' && (
                <div className="mt-4 p-4 border border-slate-200" style={{ borderLeft: '3px solid #C5A059', background: '#FBF7EF' }} data-testid="lex-ye-notes-block">
                  <label className="block text-xs uppercase tracking-widest font-semibold text-slate-600 mb-1">
                    Begründungs-Vermerk (Pflicht) *
                  </label>
                  <p className="text-xs text-slate-500 mb-2">
                    Abweichung vom Regelsatz ({year >= 2026 ? '15' : year === 2025 ? '13,5' : '12'} %): Warum gilt der 6 %-Sondersatz?
                    (z. B. kaufm./techn. Beratung, § 22 Z 2 EStG, schriftstellerische/vortragende Tätigkeit)
                  </p>
                  <textarea className="lex-input !h-20 resize-none" value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="Begründung der 6 %-Wahl …" data-testid="lex-ye-notes-input" />
                </div>
              )}
              <button className="lex-btn mt-4 flex items-center gap-2" onClick={save}
                disabled={saving || (form.variante === 'special' && !form.notes.trim())} data-testid="lex-ye-save-btn">
                <Save className="h-4 w-4" /> {saving ? 'Speichert …' : 'Speichern & neu berechnen'}
              </button>
            </section>

            {/* E1a */}
            {e1a && (
              <section className="lex-card p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    E1a — Basispauschalierung ({e1a.rate.toLocaleString('de-AT')} %, Cap {fmtEur(e1a.cap)})
                  </h3>
                  <button className="lex-btn-outline !py-1.5 !px-3 flex items-center gap-1.5 text-xs" onClick={() => downloadXml('e1')} data-testid="lex-ye-e1-xml-btn">
                    <Download className="h-3.5 w-3.5" /> E1/E1a-XML
                  </button>
                </div>
                <KzTable rows={e1a.kennzahlen} testId="lex-ye-e1a-table" />
                <div className="mt-3 pt-3 border-t-2 border-slate-200 flex justify-between text-sm">
                  <span className="font-semibold text-slate-700">Gewinn (→ E1 KZ 320)</span>
                  <span className="lex-mono font-bold" style={{ color: '#0A1128' }} data-testid="lex-ye-gewinn">{fmtEur(e1a.gewinn)}</span>
                </div>
              </section>
            )}

            {/* U1 */}
            <section className="lex-card p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs uppercase tracking-widest font-semibold text-slate-500">U1 — Umsatzsteuerjahreserklärung</h3>
                {u1 && (
                  <button className="lex-btn-outline !py-1.5 !px-3 flex items-center gap-1.5 text-xs" onClick={() => downloadXml('u1')} data-testid="lex-ye-u1-xml-btn">
                    <Download className="h-3.5 w-3.5" /> U1-XML
                  </button>
                )}
              </div>
              {u1 ? (
                <>
                  <KzTable rows={u1.kennzahlen} testId="lex-ye-u1-table" />
                  <div className="mt-3 pt-3 border-t-2 border-slate-200 space-y-1 text-sm">
                    {u1.vorsteuer_pauschalierung?.active && (
                      <div className="flex justify-between text-slate-500">
                        <span>Vorsteuerpauschale 1,8 % (§ 14 UStG)</span>
                        <span className="lex-mono">{fmtEur(u1.vorsteuer_pauschalierung.pauschale)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-slate-700">
                      <span>USt-Zahllast {year}</span>
                      <span className="lex-mono font-bold" style={{ color: '#0A1128' }} data-testid="lex-ye-zahllast">{fmtEur(u1.payable_vat)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500" data-testid="lex-ye-u1-error">{data?.u1_error || 'U1 nicht verfügbar.'}</p>
              )}
            </section>

            <p className="text-xs text-slate-400">
              FinanzOnline-Übermittlung erfolgt manuell durch die StB-GmbH (Datenstrom-Upload).
              Kennzahlen bitte vor Einreichung fachlich prüfen.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
